import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useClinicBranding, clinicLogo } from '../context/ClinicBrandingContext';
import { getAppointments, resolveMediaUrl } from '../lib/api';
import { isStaffRole, isSuperAdmin } from '../lib/adminRoles';
import { toast } from 'sonner';
import { playAdminPendingAppointmentAlert } from '../lib/adminPendingAlertSound';
import { Button } from '../components/ui/button';
import {
  Package,
  Calendar,
  ClipboardList,
  FolderOpen,
  MessageSquare,
  Settings,
  SlidersHorizontal,
  LogOut,
  Menu,
  X,
  Home,
  LayoutDashboard,
  MessageCircle,
  Globe,
  Users,
  Building2,
  Handshake,
  Receipt,
} from 'lucide-react';

const PENDING_POLL_MS = 12000;

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const { info: clinicInfo } = useClinicBranding();
  const brandLogo = resolveMediaUrl(clinicLogo(clinicInfo));
  const brandName = clinicInfo?.name?.trim() || 'Centro Cultural';
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const knownPendingIdsRef = useRef(null);
  const [pendingAppointmentCount, setPendingAppointmentCount] = useState(0);

  useEffect(() => {
    if (!loading && (!user || !isStaffRole(user.role))) {
      navigate('/admin/login');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (loading || !user || !isStaffRole(user.role)) return undefined;

    const poll = async () => {
      try {
        const list = await getAppointments('pending');
        const arr = Array.isArray(list) ? list : [];
        setPendingAppointmentCount(arr.length);
        const ids = new Set(arr.map((a) => a.id).filter(Boolean));
        if (knownPendingIdsRef.current === null) {
          knownPendingIdsRef.current = ids;
          return;
        }
        const prev = knownPendingIdsRef.current;
        const newcomerCount = [...ids].filter((id) => !prev.has(id)).length;
        if (newcomerCount > 0) {
          toast.info(
            newcomerCount === 1
              ? 'Nueva cita pendiente de confirmación'
              : `${newcomerCount} nuevas citas pendientes de confirmación`,
            {
              description:
                'Revisa Citas para confirmarlas por WhatsApp o cancelarlas. El paciente recibirá el aviso cuando confirmes.',
              duration: 10000,
              action: {
                label: 'Ver citas',
                onClick: () => navigate('/admin/citas/por-confirmar'),
              },
            }
          );
          playAdminPendingAppointmentAlert();
        }
        knownPendingIdsRef.current = ids;
      } catch {
        /* red intermitente: no inundar la UI */
      }
    };

    poll();
    const t = window.setInterval(poll, PENDING_POLL_MS);
    return () => window.clearInterval(t);
  }, [loading, user, navigate]);

  if (loading || !user || !isStaffRole(user.role)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">
          {loading ? 'Cargando…' : 'Redirigiendo al inicio de sesión…'}
        </div>
      </div>
    );
  }

  const navItems = [
    { name: 'Dashboard', path: '/admin', icon: LayoutDashboard, badge: null },
    { name: 'Productos', path: '/admin/productos', icon: Package, badge: null },
    { name: 'Expedientes', path: '/admin/expedientes', icon: FolderOpen, badge: null },
    { name: 'Citas', path: '/admin/citas', icon: Calendar, badge: null },
    {
      name: 'Citas por confirmar',
      path: '/admin/citas/por-confirmar',
      icon: ClipboardList,
      badge: 'pendingCount',
    },
    { name: 'Reseñas', path: '/admin/resenas', icon: MessageSquare, badge: null },
    { name: 'Tipos de cita', path: '/admin/tipos-cita', icon: Building2, badge: null },
    { name: 'Convenios', path: '/admin/convenios', icon: Handshake, badge: null },
    { name: 'Cierres', path: '/admin/horarios', icon: Settings, badge: null },
    { name: 'WhatsApp', path: '/admin/whatsapp', icon: MessageCircle, badge: null },
    { name: 'Facturas', path: '/admin/facturas', icon: Receipt, badge: null },
    { name: 'Configura Sitio', path: '/admin/configura-sitio', icon: Globe, badge: null },
    { name: 'Settings', path: '/admin/settings', icon: SlidersHorizontal, badge: null },
    ...(isSuperAdmin(user.role)
      ? [{ name: 'Administradores', path: '/admin/administradores', icon: Users, badge: null }]
      : []),
  ];

  const isActive = (path) => {
    const p = location.pathname;
    if (path === '/admin/citas/por-confirmar') return p === path || p.startsWith(`${path}/`);
    if (path === '/admin/citas') return p.startsWith(path) && !p.startsWith('/admin/citas/por-confirmar');
    if (path === '/admin') return p === '/admin';
    return p.startsWith(path);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-layout">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-border px-4 py-3 flex items-center justify-between">
        <img src={brandLogo} alt={brandName} className="h-10 max-w-[160px] object-contain" />
        <button onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-sidebar-toggle">
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-40 h-screen w-64 bg-white border-r border-border transition-transform ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
        data-testid="admin-sidebar"
      >
        <div className="flex flex-col h-full min-h-0">
          <div className="shrink-0 p-4 lg:p-6 border-b border-border hidden lg:block">
            <img src={brandLogo} alt={brandName} className="h-10 lg:h-12 max-w-[200px] object-contain" />
            <p className="text-xs text-muted-foreground mt-2">
              {brandName}
              {isSuperAdmin(user.role) ? ' · Super admin' : ''}
            </p>
          </div>

          <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 lg:p-4 space-y-0.5 mt-16 lg:mt-0">
            {navItems.map((item) => {
              const count =
                item.badge === 'pendingCount'
                  ? pendingAppointmentCount
                  : 0;
              const badgeLabel =
                count > 99 ? '99+' : count > 0 ? String(count) : null;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 lg:px-4 lg:py-3 rounded-xl text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-sage-50 hover:text-foreground'
                  }`}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <span className="relative shrink-0 inline-flex items-center justify-center w-9 h-9">
                    <item.icon className="w-5 h-5" />
                    {badgeLabel ? (
                      <span
                        className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] leading-[18px] font-bold text-center shadow-sm border ${
                          isActive(item.path)
                            ? 'bg-red-600 text-white border-white/90'
                            : 'bg-[#EA4335] text-white border-white'
                        }`}
                        aria-label={`${count} citas por confirmar`}
                      >
                        {badgeLabel}
                      </span>
                    ) : null}
                  </span>
                  <span className="flex-1 truncate">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          <div className="shrink-0 p-3 lg:p-4 border-t border-border space-y-1 bg-white">
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-muted-foreground hover:bg-sage-50 hover:text-foreground transition-colors"
              data-testid="nav-home"
            >
              <Home className="w-5 h-5" />
              Ver Sitio
            </Link>
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 px-4 text-accent hover:text-accent hover:bg-accent/10"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
